import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { faGripDots } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PropsWithChildren, useMemo } from 'react';

import './styles.scss';

interface NestedDragContainerProps {
  items: any[];
  onItemsChange: (newItems: any[]) => void;
  parentId?: string;
}

interface NestedDragItemProps {
  item: any;
  onChildItemsChange: (childArr: any[]) => void;
  parentId?: string;
}

export function NestedDragContainer({
  items,
  onItemsChange,
  parentId,
}: PropsWithChildren<NestedDragContainerProps>) {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const currentIds = useMemo<UniqueIdentifier[]>(() => items.map((it) => it.id), [items]);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;

    if (active.id === over.id) return;

    const oldIndex = items.findIndex((it) => it.id === active.id);
    const newIndex = items.findIndex((it) => it.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newArr = arrayMove(items, oldIndex, newIndex);
      onItemsChange(newArr);
    }
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <SortableContext items={currentIds}>
        {items.map((child) => (
          <NestedDragItem
            key={child.id}
            item={child}
            parentId={parentId}
            onChildItemsChange={(newChildren) => {
              const idx = items.findIndex((it) => it.id === child.id);
              if (idx === -1) return;
              const copy = [...items];
              copy[idx] = { ...child, children: newChildren };
              onItemsChange(copy);
            }}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}

function NestedDragItem({
  item,
  onChildItemsChange,
  parentId,
}: PropsWithChildren<NestedDragItemProps>) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: item.id,
    data: { parentId },
  });

  const modTransform = transform ? { ...transform, scaleX: 1, scaleY: 1 } : transform;

  const style = {
    transform: CSS.Transform.toString(modTransform),
    transition,
    marginLeft: 18,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="nested-drag-item">
      <div className="nested-drag-item-label">
        <FontAwesomeIcon icon={faGripDots} className="icon" />
        <span>{item.label}</span>
      </div>
      {item.children && item.children.length > 0 && (
        <NestedDragContainer
          items={item.children}
          onItemsChange={onChildItemsChange}
          parentId={item.id}
        />
      )}
    </div>
  );
}
